import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogTitle,
	Label,
	NativeSelect,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import { showErrorNotification } from "molecules/notifications/notificationEvents";
import { DialogActions } from "molecules/dialog/DialogActions";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import type { LegalEntity } from "src/common/service/nexus/endpoints/companyDomain/types.js";

interface Props {
	isOpen: boolean;
	legalEntity: LegalEntity | undefined;
	availableParents: LegalEntity[];
	onMove: (parentId: string) => Promise<void>;
	onCancel: () => void;
}

// Keyboard/touch-accessible alternative to dragging a row onto a new parent —
// the drag interaction has no non-pointer equivalent otherwise.
export const MoveCompanyToParentModal = ({
	isOpen,
	legalEntity,
	availableParents,
	onMove,
	onCancel,
}: Props) => {
	const { t } = useTranslation();

	const [selected, setSelected] = useState("");
	const [isMoving, setIsMoving] = useState(false);

	// The dialog stays mounted while closed, so a stale selection from a
	// previous open (possibly no longer a valid parent) must not carry over.
	useEffect(() => {
		if (isOpen) {
			setSelected("");
		}
	}, [isOpen]);

	const formId = useId();
	const parentInputId = useId();

	return (
		<Dialog onClose={onCancel} open={isOpen}>
			<DialogContent>
				<DialogTitle>{t("Move company")}</DialogTitle>
				<DialogBody className="pb-1">
					<form
						onSubmit={async (e) => {
							e.preventDefault();
							if (!selected) {
								return;
							}
							setIsMoving(true);
							try {
								await onMove(selected);
							} catch {
								showErrorNotification({ message: t("Error") });
							} finally {
								setIsMoving(false);
							}
						}}
						id={formId}
						className="space-y-4"
					>
						<div>
							<Label htmlFor={parentInputId}>
								{t("Move {{name}} to a new parent company", {
									name: legalEntity?.name ?? "",
								})}
							</Label>
							<NativeSelect
								onChange={(e) => setSelected(e.target.value)}
								value={selected}
								id={parentInputId}
							>
								<option value="">{t("Select parent company")}</option>
								{availableParents.map((node) => (
									<option key={node.id} value={node.id}>
										{node.name}
									</option>
								))}
							</NativeSelect>
						</div>
					</form>
				</DialogBody>
				<DialogActions withCancelButton>
					<Button
						disabled={!selected}
						type="submit"
						form={formId}
						isLoading={isMoving}
					>
						{t("Move")}
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
};
